import {isKeyPressed, keyCodes} from '@ey/utils';
import PropTypes from 'prop-types';
import React, {Fragment, useCallback, useEffect, useState, useContext} from 'react';
import {useDispatch} from 'react-redux';
import styled from 'styled-components';
import {clientSideEventNames} from '../../../util/uiconstants';
import {isValidFunction, labels} from '../../../util/utils';
import FormField from '../BaseComponentWrappers/MotifFormField';
import Label from '../BaseComponentWrappers/MotifLabel';
import ProgressBar from '../BaseComponentWrappers/MotifProgressBar';
import TextArea from '../BaseComponentWrappers/MotifTextArea';
import CanvasFormInputButtons from './CanvasFormInputButtons';
import CanvasFormInputDisplay from './CanvasFormInputDisplay';
import CanvasFormTextAreaValue from './CanvasFormTextAreaValue';
import {getUnresolvedCommentsCount} from '../../../actions/ValidationControl/validationcontrolactions';
import {DocumentTypeIdContext} from '../../Main/Forms/CanvasFormContainer';
import {useDocumentId, useEngagementId} from '../../../util/customHooks';

export default function CanvasFormTextArea({
	children,
	className,
	cols,
	disabled,
	id,
	isReadOnly,
	label,
	showLabel,
	maxLength,
	minLength,
	name,
	onAccept,
	onBlur,
	onCancel,
	onChange,
	onChanged,
	onClick,
	onFocus,
	onKeyDown,
	onKeyPress,
	placeholder,
	progressBarClassName,
	progressBarLabel,
	required,
	rows,
	showProgressBar,
	value,
	textAreaError,
	textAreaErrorMessage,
	isAresView,
	showPlainText,
	showRightCounter,
	count,
	relateToRiskRationaleMaxCharacters
}) {
	const [isEditing, setIsEditing] = useState(false);
	const [hideLabel, setHideLabel] = useState(false);

	const handleOnClick = useCallback((event) => {
		event.stopPropagation();
		
		if (!isReadOnly) {
			setIsEditing(true);
		}
		if (isValidFunction(onClick)) {
			onClick(event);
		}
	}, []);

	const dispatch = useDispatch();
	const engagementId = useEngagementId(),
		documentId = useDocumentId(),
		documentTypeId = useContext(DocumentTypeIdContext);

	const [isSaving, setIsSaving] = useState(false);
	const [bodyFreeText, setBodyFreeText] = useState(value);

	const handleOnChange = useCallback(
		(event) => {
			onChange && onChange(event);
			setBodyFreeText(event.target.value);
		},
		[onChange]
	);

	const handleOnAccept = useCallback(
		(val, evt) => {
			let promise;

			if (isValidFunction(onAccept)) {
				setIsSaving(true);

				promise = onAccept(bodyFreeText);

				if (promise instanceof Promise) {
					promise
						.then(() => {
							if (isValidFunction(onChanged)) {
								onChanged(id, value);
								dispatch(getUnresolvedCommentsCount(engagementId, documentId, documentTypeId));
							}
							dispatch(getUnresolvedCommentsCount(engagementId, documentId, documentTypeId));
						})
						.finally(() => {
							setIsSaving(false);
							setIsEditing(false);
							// onCancel should not be called here!!
						});
				} else {
					setIsEditing(false);
					setIsSaving(false);
				}
			} else {
				setIsEditing(false);
				setIsSaving(false);
			}
		},
		[bodyFreeText, onAccept, onChanged]
	);

	const handleOnCancel = useCallback(
		(val, evt) => {
			setIsEditing(false);
			setBodyFreeText(value);
			if (isValidFunction(onCancel)) {
				onCancel();
			}
		},
		[value, onCancel]
	);

	const handleOnKeyDown = useCallback(
		(event) => {
			if (isKeyPressed(event.nativeEvent, keyCodes.ESCAPE)) {
				handleOnCancel();
			}

			if (!event.shiftKey && isKeyPressed(event.nativeEvent, keyCodes.ENTER)) {
				event.preventDefault();
				event.stopPropagation();

				handleOnAccept();
				return;
			}

			if (isValidFunction(onKeyDown)) {
				onKeyDown(event);
			}

			if (isValidFunction(onKeyPress)) {
				onKeyPress(event);
			}
		},
		[handleOnAccept, handleOnCancel, onKeyDown, onKeyPress]
	);

	const handleGetValidations = useCallback((event) => {
		setIsSaving(false);
	}, []);

	const handleOnFocus = useCallback((event) => {
		setHideLabel(true);
		if (isValidFunction(onFocus)) {
			onFocus(event);
		}
	});

	useEffect(() => {
		window.addEventListener(clientSideEventNames.getValidations, handleGetValidations);
		return () => {
			window.removeEventListener(clientSideEventNames.getValidations, handleGetValidations);
		};
	}, [handleGetValidations]);

	// TODO: Try to remove
	useEffect(() => {
		if (value !== bodyFreeText) {
			setBodyFreeText(value);
		}
	}, [value]);

	useEffect(() => {
		if (disabled && isEditing) {
			setIsEditing(false);
		}
	}, [disabled]);

	useEffect(() => {
		setTimeout(() => {
			const elementId = isEditing ? `textArea-${id}` : `canvasFormInputDisplay-${id}`;
			const element = document.getElementById(elementId);
			if (element) element.focus();
		}, 0);
	}, [isEditing, id]);

	return (
		<StyledCanvasFormTextArea
			className={`${className} ${isAresView && 'aresStyledCanvasFormTextArea'} ${!isReadOnly &&
				isEditing &&
				'aresStyledCanvasFormTextAreaEditing'}`}
			isEditing={isEditing}
			isReadOnly={isReadOnly}
		>
			<FormField className={`canvas-form-field ${bodyFreeText ? 'styledCanvasFormTextAreaValue' : ''}`}>
				{isSaving && showProgressBar && (
					<ProgressBar
						className={`greyOutArea ${progressBarClassName}`}
						labelComponent={<span>{progressBarLabel}</span>}
					/>
				)}
				{!isReadOnly && isEditing ? (
					<>	
						{!bodyFreeText && !hideLabel && <Label position="in">{label}</Label>}
						<TextArea
							cols={cols}
							disabled={disabled}
							id={`textArea-${id}`}
							maxLength={maxLength}
							minLength={minLength}
							name={name}
							onBlur={onBlur}
							onChange={handleOnChange}
							onClick={handleOnClick}
							onFocus={handleOnFocus}
							onKeyDown={handleOnKeyDown}
							placeholder={placeholder}
							readOnly={isReadOnly}
							required={required}
							rows={rows}
							value={bodyFreeText}
							textAreaError={textAreaError}
							textAreaErrorMessage={textAreaErrorMessage}
							hideClearButton={true}
						/>
						{isEditing && !isSaving && !disabled && (
							<CanvasFormInputButtons value={value} onAccept={handleOnAccept} onCancel={handleOnCancel} />
						)}
					</>
				) : (
					<CanvasFormInputDisplay
						id={`canvasFormInputDisplay-${id}`}
						value={value}
						label={label}
						showLabel={showLabel}
						isReadOnly={isReadOnly}
						onClick={handleOnClick}
						className="customScrollbar"
						isAresView={isAresView}
						showPlainText={showPlainText}
						hideLabel={hideLabel}
					>
						{children || <CanvasFormTextAreaValue />}
					</CanvasFormInputDisplay>
				)}
			</FormField>
			{showRightCounter && (
				<span className="pull-right-counter">{`${count}/${relateToRiskRationaleMaxCharacters}`}</span>
			)}
		</StyledCanvasFormTextArea>
	);
}

CanvasFormTextArea.propTypes = {
	className: PropTypes.string,
	cols: PropTypes.number,
	disabled: PropTypes.bool,
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	label: PropTypes.string,
	showLabel: PropTypes.bool,
	maxLength: PropTypes.number,
	minLength: PropTypes.number,
	name: PropTypes.string,
	onAccept: PropTypes.func.isRequired,
	onBlur: PropTypes.func,
	onCancel: PropTypes.func,
	onChange: PropTypes.func,
	onClick: PropTypes.func,
	onKeyDown: PropTypes.func,
	onKeyPress: PropTypes.func,
	placeholder: PropTypes.string,
	progressBarLabel: PropTypes.string,
	required: PropTypes.bool,
	rows: PropTypes.number,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	isReadOnly: PropTypes.bool,
	progressBarClassName: PropTypes.string,
	showProgressBar: PropTypes.bool
};

CanvasFormTextArea.defaultProps = {
	className: 'canvas-form-textarea',
	disabled: false,
	hideClearButton: true,
	isReadOnly: false,
	showLabel: false,
	progressBarClassName: 'canvas-form-textarea-progress-bar',
	progressBarLabel: labels.saving,
	showProgressBar: true,
	required: false,
	value: ''
};

const StyledCanvasFormTextArea = styled.section`
	display: flex;
	flex-direction: row;
	position: relative;
	width: var(--px-656);

	.StyledCanvasFormInputDisplay {
		height: var(--px-94) !important;
		padding: 0 var(--px-7);
		width: 100%;
	}
	&.aresStyledCanvasFormTextArea {
		.motif-form-field {
			.StyledCanvasFormInputDisplay {
				.canvas-form-input-display-value {
					padding: 0 0 0 var(--px-8);
					height: 100%;
					line-height: var(--px-20);
					font-size: var(--px-14);
					font-weight: 300;
					position: relative;
					section {
						padding: var(--px-10) var(--px-5) var(--px-5) 0;
					}
				}
			}
		}
		&:hover {
			border-color: var(--neutrals-900);
		}
	}
	&.aresStyledCanvasFormTextAreaEditing {
		flex-direction: column;
		textarea {
			padding-right: var(--px-65);
			overflow-y: auto;
			&::-webkit-scrollbar {
				height: var(--px-6);
				width: var(--px-6);
			}
			&::-webkit-scrollbar-track {
				background: var(--neutrals-200);
				height: var(--px-6);
				width: var(--px-6);
				border-radius: 0 var(--px-8) var(--px-8) 0;
				margin-bottom: var(--px-2);
			}
			&::-webkit-scrollbar-thumb {
				background: var(--neutrals-300);
				height: var(--px-6);
				width: var(--px-6);
				cursor: pointer;
				border-radius: 0 var(--px-8) var(--px-8) 0;
			}
			&::-webkit-scrollbar-thumb:hover {
				background: var(--neutrals-700);
			}
		}
		.StyledConfirmInputIcons {
			justify-content: flex-end;
			position: absolute;
			right: var(--px-16);
			top: var(--px-16);
		}
	}
`;
